import type React from 'react';
import { type ScheduledTask } from '../../types/tasks';
import { DurationBadge, ProgressBar, StatusBadge, TimeDisplay } from '../molecules';
import { tasksService } from '../../services/tasks';

interface TaskCardProps {
  task: ScheduledTask;
  className?: string;
  onClick?: () => void;
  showProgress?: boolean;
  showTimeUntil?: boolean;
  variant?: 'default' | 'compact' | 'detailed';
}

const getProgressBarVariant = (progress: number): 'success' | 'default' | 'warning' => {
  if (progress >= 100) {
    return 'success';
  }
  if (progress >= 50) {
    return 'default';
  }
  return 'warning';
};

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  className = '',
  onClick,
  showProgress = true,
  showTimeUntil = false,
  variant = 'default'
}) => {
  const categoryDisplayName = tasksService.getCategoryDisplayName(task.category);
  const categoryColorClass = tasksService.getCategoryColorClass(task.category);
  const priorityInfo = tasksService.getPriorityDisplayInfo(task.priority);
  
  const timeUntil = showTimeUntil ? tasksService.getTimeUntilTask(task.targetTimeHour, task.targetTimeMinute) : null;
  const estimatedEndTime = tasksService.getEstimatedCompletionTime(
    task.targetTimeHour, 
    task.targetTimeMinute, 
    task.estimatedDuration
  );

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((event.key === 'Enter' || event.key === ' ') && onClick) {
      event.preventDefault();
      onClick();
    }
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'compact':
        return 'p-3';
      case 'detailed':
        return 'p-6';
      default:
        return 'p-4';
    }
  };

  const renderCompactView = () => (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex-shrink-0">
          <TimeDisplay 
            hour={task.targetTimeHour} 
            minute={task.targetTimeMinute}
            className="text-gray-600"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 truncate">
            {task.displayName || task.name}
          </h4>
          <div className="flex items-center gap-2 mt-1">
            <span className={`px-2 py-0.5 text-xs rounded-full ${categoryColorClass}`}>
              {categoryDisplayName}
            </span>
            <DurationBadge duration={task.estimatedDuration} variant="compact" />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <StatusBadge status={task.status as 'good' | 'neutral' | 'bad' | 'ahead' | 'upward' | 'maintaining' | 'slowing' | 'declining' | 'none'} size="sm" />
        {onClick && (
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        )}
      </div>
    </div>
  );

  const renderDefaultView = () => (
    <>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
            {task.displayName || task.name}
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${categoryColorClass}`}>
              {categoryDisplayName}
            </span>
            {task.priority !== 'regular' && (
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                task.priority === 'high' 
                  ? 'bg-red-100 text-red-800 border-red-200'
                  : 'bg-gray-100 text-gray-600 border-gray-200'
              }`}>
                {priorityInfo.icon} {priorityInfo.text}
              </span>
            )}
          </div>
        </div>
        <StatusBadge status={task.status as 'good' | 'neutral' | 'bad' | 'ahead' | 'upward' | 'maintaining' | 'slowing' | 'declining' | 'none'} size="sm" />
      </div>

      {/* Description */}
      {task.description && (
        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
          {task.focusedDescription ?? task.description}
        </p>
      )}

      {/* Time and Duration Info */}
      <div className="flex items-center justify-between text-sm mb-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 text-gray-600">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            <TimeDisplay 
              hour={task.targetTimeHour} 
              minute={task.targetTimeMinute}
            />
            {timeUntil && timeUntil !== 'now' && (
              <span className="text-xs text-gray-500">({timeUntil})</span>
            )}
          </div>
          <DurationBadge duration={task.estimatedDuration} />
        </div>
        <div className="text-xs text-gray-500">
          Until {estimatedEndTime}
        </div>
      </div>

      {/* Progress */}
      {showProgress && task.progress !== undefined && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>Progress</span>
            <span>{task.progress}%</span>
          </div>
          <ProgressBar
            progress={task.progress}
            variant={getProgressBarVariant(task.progress)}
            size="sm"
            animated
          />
        </div>
      )}

      {/* Advice */}
      {task.advice && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-blue-800">{task.advice}</p>
          </div>
        </div>
      )}
    </>
  );

  const renderDetailedView = () => (
    <>
      {renderDefaultView()}
      
      {/* Additional Details for Detailed View */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Created:</span>
            <div className="font-medium">
              {new Date(task.createdAt).toLocaleDateString()}
            </div>
          </div>
          <div>
            <span className="text-gray-500">Updated:</span>
            <div className="font-medium">
              {new Date(task.updatedAt).toLocaleDateString()}
            </div>
          </div>
        </div>
        
        {task.nextScheduledTime && (
          <div className="mt-3">
            <span className="text-gray-500 text-sm">Next scheduled:</span>
            <div className="font-medium">
              {new Date(task.nextScheduledTime).toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div
      className={`
        bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200
        ${onClick ? 'cursor-pointer hover:border-gray-300' : ''}
        ${getVariantClasses()}
        ${className}
      `}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
      aria-label={onClick ? `View details for ${task.name}` : undefined}
    >
      {variant === 'compact' && renderCompactView()}
      {variant === 'default' && renderDefaultView()}
      {variant === 'detailed' && renderDetailedView()}
    </div>
  );
};